<template>
  <div class="sticky top-16 z-50 bg-white flex flex-col">
    <template v-if="!hasGodModeShift">
      <IncomingConsultationBanner
        v-for="item in emergencyConsultations"
        :key="item.id"
        :consultation="item"
      />
      <CallInProgressBanner
        v-if="consultationsWithActiveCall.length > 0"
        :consultation-requests="consultationsWithActiveCall"
      />
      <IncomingConsultationBanner
        v-if="singleIncomingConsultation"
        :consultation="singleIncomingConsultation"
      />
      <ConsultationsOverviewBanner
        v-else-if="mergedIncomingConsultations.length > 1"
        :direct-consultations="standardDirectConsultations"
        :scheduled-consultations="incomingScheduledConsultations"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import CallInProgressBanner from '@/components/consultationrequest/CallInProgressBanner.vue'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import { storeToRefs } from 'pinia'
import { useDynamicPolling } from '@/composables/useDynamicPolling'
import {
  getRefreshIntervalIncomingRequests,
  getRefreshIntervalOutgoingRequests
} from '@/global/environmentGetters'
import { compareConsultationRequests } from '@/util/compareConsultationRequests'
import { computed } from 'vue'
import {
  type ConsultationRequestDTO,
  ConsultationRequestStatusDTO,
  type ScheduledConsultationDTO,
  ScheduledConsultationParticipantStatusDTO,
  UrgencyDTO
} from '@/generated'

import { useUserStore } from '@/stores/user'
import { useCallStore } from '@/stores/call'
import ConsultationsOverviewBanner from './ConsultationsOverviewBanner.vue'
import IncomingConsultationBanner from '@/components/consultationrequest/IncomingConsultationBanner.vue'
import { useRepeatWhileMounted } from '@/composables/repeatWhileMounted'

const {
  getConsultationRequestsWithActiveCall,
  getScheduledConsultationsWithActiveCall,
  incomingDirectConsultations,
  incomingScheduledConsultations
} = storeToRefs(useConsultationRequestStore())
const { hasGodModeShift, user } = storeToRefs(useUserStore())
const { callConsultationRequestId } = storeToRefs(useCallStore())

useRepeatWhileMounted(
  useConsultationRequestStore().refreshIncomingConsultationRequestList,
  getRefreshIntervalIncomingRequests()
)

useDynamicPolling(
  useConsultationRequestStore().refreshOutgoingConsultationRequestList,
  getRefreshIntervalOutgoingRequests()
)

const emergencyConsultations = computed<ConsultationRequestDTO[]>(() => {
  return incomingDirectConsultations.value
    .filter(
      (cr) =>
        cr.urgency === UrgencyDTO.EMERGENCY && cr.status === ConsultationRequestStatusDTO.ACCEPTED
    )
    .sort(compareConsultationRequests)
})

const standardDirectConsultations = computed<ConsultationRequestDTO[]>(() => {
  return incomingDirectConsultations.value
    .filter(
      (cr) =>
        cr.status === ConsultationRequestStatusDTO.CREATED && cr.urgency !== UrgencyDTO.EMERGENCY
    )
    .sort(compareConsultationRequests)
})

const incomingPendingScheduledConsultations = computed<ScheduledConsultationDTO[]>(() => {
  return incomingScheduledConsultations.value.filter((consultation) => {
    const participant = consultation.participants.find(
      (p) => p.user.userName === user.value?.userName
    )

    return participant?.participationStatus === ScheduledConsultationParticipantStatusDTO.PENDING
  })
})

const mergedIncomingConsultations = computed(() => {
  return [...standardDirectConsultations.value, ...incomingPendingScheduledConsultations.value]
})

const singleIncomingConsultation = computed<
  ConsultationRequestDTO | ScheduledConsultationDTO | null
>(() => {
  if (mergedIncomingConsultations.value.length === 1) {
    return mergedIncomingConsultations.value[0]
  }

  return null
})

const consultationsWithActiveCall = computed<(ConsultationRequestDTO | ScheduledConsultationDTO)[]>(
  () => {
    const sortedDirectConsultations = [
      ...getConsultationRequestsWithActiveCall.value.filter(
        (cr) =>
          !(
            cr.urgency === UrgencyDTO.EMERGENCY &&
            cr.status === ConsultationRequestStatusDTO.ACCEPTED
          )
      )
    ].sort(compareConsultationRequests)

    return [...sortedDirectConsultations, ...getScheduledConsultationsWithActiveCall.value].filter(
      (cr) => cr.id !== callConsultationRequestId.value && !cr.isUserInCall
    )
  }
)
</script>
